import { MessageCircle, Target, Mail, Calendar, BookOpen, FolderOpen, Mic, FileCheck, CalendarDays, StickyNote, Contact, UserCircle } from '../../icons';
import type { UsageSource } from '../../api/workspace';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';

/**
 * 출처별 스마트 폴더 (2026-08-14) — 사용자 요구:
 *
 *   "내 파일에 폴더를 기본구성 — 채팅, AI회의록, 프로젝트 폴더를 만들어 주고
 *    해당 목록을 보여준다."
 *
 * **실제 폴더가 아니라 가상 폴더다.** 실제로 만들려면 파일 174개의 `parentId` 를 옮겨야 하는데
 * 되돌릴 수 없고, 한 파일이 여러 곳에 동시에 쓰일 수 있다(채팅에 올린 파일을 프로젝트 업무에
 * 첨부). 실제 폴더는 둘 중 하나를 고르라고 강요하지만 가상 폴더는 양쪽에 다 보인다.
 *
 * `정리 대상`(어디에도 안 쓰임)이 이 기능의 실질적 값이다 — 안전하게 지울 수 있는 파일을
 * 지금은 가려낼 방법이 없다.
 *
 * ⚠ 0건 소스는 서버가 아예 보내지 않는다. 빈 폴더를 띄워 두면 "왜 비어 있지" 를 매번
 *   확인하게 된다. AI 회의록이 지금 그 경우다 — 회의록은 파일을 참조하지 않는다.
 */

export interface SmartMeta {
  icon: typeof MessageCircle;
  labelKey: WorkspaceI18nKey;
  tone: string;
}

/**
 * 출처별 아이콘·이름 (2026-08-14 export, 2026-08-19 확장 가능하게 바꿈).
 *
 * 좌측 네비의 '내 파일' 하위 목록이 같은 표를 쓴다 — 같은 것을 두 곳에서 다르게 부르면
 * (한쪽은 '채팅', 다른 쪽은 '메시지') 같은 목록인 줄 모른다.
 *
 * ## 새 출처가 생기면 (2026-08-19)
 *
 * 목록은 **서버가 준다**. 서버 `workspace-usage.ts` 의 참조처 선언에 한 줄을 더하면
 * 그 순간부터 `smartFolders` 에 실려 오고, 화면은 **아무 수정 없이** 새 항목을 그린다.
 * 여기 표는 "이름과 아이콘을 예쁘게 붙이는" 자리일 뿐 동작 조건이 아니다 —
 * 표에 없는 출처는 `smartMetaOf()` 가 기본 아이콘과 이름으로 받아 낸다.
 *
 * 아래 **후보**들은 아직 서버가 세지 않지만(참조 테이블이 없거나 첨부 컬럼이 없다)
 * 붙는 즉시 제대로 보이도록 미리 등록해 둔 것이다. 미리 등록해도 비용은 0이다 —
 * 서버가 안 보내면 그릴 일도 없다.
 */
export const SMART_META: Partial<Record<string, SmartMeta>> = {
  // ── 지금 서버가 세는 것 ──
  chat: { icon: MessageCircle, labelKey: 'workspace.smart.chat', tone: 'var(--k-word)' },
  task: { icon: Target, labelKey: 'workspace.smart.task', tone: 'var(--k-ppt)' },
  mail: { icon: Mail, labelKey: 'workspace.smart.mail', tone: 'var(--k-excel)' },
  calendar: { icon: Calendar, labelKey: 'workspace.smart.calendar', tone: 'var(--k-md)' },
  board: { icon: BookOpen, labelKey: 'workspace.smart.board', tone: 'var(--k-img)' },
  /*
   * 명함첩 사진 (2026-10-07) — ⭐ 「후보」가 아니라 **이미 서버가 센다**:
   *   `workspace-usage.ts:81` `USAGE_KINDS` 에 `'contact'` · `:89` `FK_SOURCE_KINDS` 에도 있다
   *   (그 파일 머리말이 「둘 중 하나만 하면 조용히 아무것도 안 나온다」고 적어 두었고, 둘 다 됐다).
   * ⚠ 아이콘은 **레일·빠른실행과 같은 것**을 쓴다(`NavBar.tsx:123` · `quick-links.tsx:38` 의 `Contact`) —
   *   같은 것을 두 곳에서 다르게 그리면 같은 데로 가는 줄 모른다(이 표 머리말의 그 까닭 그대로).
   * ⚠ 이름이 형제들의 「… 첨부」 꼴이 **아니다.** 명함 사진은 첨부가 아니라 **그 명함 자체**라서다.
   *   `none`(「정리 대상」)이 이미 그 꼴을 벗어나 있으므로, 이 표의 잣대는 「첨부인가」가 아니라
   *   **「무엇으로 쓰이는 파일인가」**다. ⇒ ko 는 그릇을 가리키는 「명함첩」이다(2026-10-01 결정).
   */
  contact: { icon: Contact, labelKey: 'workspace.smart.contact', tone: 'var(--k-img)' },
  /*
   * ⭐ 2026-10-07 — **어제 비워 둔 `ecard` 를 채웠다.** 비워 둔 까닭이 «녹았다»:
   *   어제는 「한국어 이름을 지으면 `contacts-i18n` 의 ⏸ ⓑ 동결을 여는 일」이라고 보았는데,
   *   재어 보니 **「전자명함」은 이미 살아 있는 메뉴 이름**이었다 — `admin_menu_registry` 의
   *   모바일 전용 메뉴 넷 중 하나이고 2026-09-15 사용자 결정(R6-12)으로 그렇게 불린다
   *   (`lib/menu-config.ts:42`). ⇒ ★ 그러므로 이것은 **이름을 짓는 일이 아니라 재사용**이고,
   *     ⓑ(맨낱말 「명함」의 두 뜻 · `mail-i18n` 의 `vcard*`)는 **그대로 보류**다.
   * ⚠ 그 메뉴 이름이 바뀌면 이 줄도 따라간다 — 출처가 거기다.
   */
  ecard: { icon: UserCircle, labelKey: 'workspace.smart.ecard', tone: 'var(--k-img)' },
  none: { icon: FolderOpen, labelKey: 'workspace.smart.none', tone: 'var(--text-subtle)' },

  // ── 후보: 서버가 켜는 즉시 자동으로 목록에 뜬다 ──
  // 회의록은 지금 파일을 참조하지 않는다(STT 원문·요약만) — 첨부가 생기면 켜진다.
  minutes: { icon: Mic, labelKey: 'workspace.smart.minutes', tone: 'var(--k-md)' },
  // 전자결재 문서에 첨부 컬럼이 생기면.
  approval: { icon: FileCheck, labelKey: 'workspace.smart.approval', tone: 'var(--k-pdf)' },
  // 예약(자원·회의실)에 첨부가 붙으면.
  reservation: { icon: CalendarDays, labelKey: 'workspace.smart.reservation', tone: 'var(--k-excel)' },
  // 메모 첨부.
  memo: { icon: StickyNote, labelKey: 'workspace.smart.memo', tone: 'var(--k-img)' },

  /*
   * ## ⭐ 이 표의 잣대 — **「서버가 켤 수 있는 «모든» 종류」로 미리 둔다**
   *
   * 「후보를 미리 등록해도 비용 0」이라는 위 머리말이 실제로 값을 했다: `minutes` 가 미리 깔려
   * 있어서 서버가 켜는 순간 「회의록」으로 떴고, 백엔드가 `voice` 로 쓰려던 이름을 그 줄이
   * `minutes` 로 되돌렸다.
   * ⇒ ⛔ 거꾸로 `contact`·`ecard` 는 **미리 안 두어서** 서버가 켠 뒤(#2218) 사용자가 **영문 낱말**을
   *   보는 자리가 생겼다(2026-10-07에 그것을 고친 것이 위 두 줄이다).
   * ⇒ ★ 그래서 자물쇠를 두었다 — `WorkspaceSmartFolders.registry.test.tsx` 가 **서버 정본의
   *   `USAGE_KINDS` 를 읽어** 이 표와 `workspaceUsageMeta.ts` **둘 다**에 다 있는지 센다.
   *   ⇒ ⭐ 다음에 백엔드가 종류를 켜면 **그때 빨개진다** — 사용자가 겪기 «전»에.
   *
   * ⚠ **어제(2026-10-07 앞) 이 자리에 「`ecard` 는 일부러 비웠다」고 적어 두었는데 지웠다.**
   *   그 까닭(「이름을 지으면 ⓑ 동결을 연다」)이 **재어 보니 틀렸기 때문**이다 — 「전자명함」은
   *   이미 살아 있는 메뉴 이름이라 **짓는 일이 아니라 재사용**이었다. 그 전말은 위 `ecard` 줄의
   *   주석에 남겼다(여기에 두면 「비워 둔 자리」로 읽힌다).
   */
};

/**
 * 출처 하나의 표시 정보 — **모르는 값이 와도 반드시 무언가를 돌려준다.**
 *
 * ⚠ 이 함수가 이 파일의 요점이다. 예전엔 `SMART_META[source]` 를 그대로 읽어서,
 *   서버가 새 출처를 보내면 `meta.icon` 이 undefined 가 되고 좌측 네비 전체가
 *   렌더 도중 터졌다(화면이 통째로 빈다). 새 출처를 켜는 일이 프론트 배포와
 *   묶여 있으면 "자동 매핑" 이 아니다.
 *
 * 모르는 출처의 이름은 `workspace.smart.<source>` 키를 찾아보고, 사전에도 없으면
 * tWorkspace 가 키를 그대로 돌려주므로 마지막에 출처 이름 자체로 눕힌다.
 */
export function smartMetaOf(source: string): SmartMeta {
  const known = SMART_META[source];
  if (known) return known;
  return {
    icon: FolderOpen,
    labelKey: `workspace.smart.${source}` as WorkspaceI18nKey,
    tone: 'var(--text-subtle)',
  };
}

/** 화면에 쓸 이름 — 사전에 없으면 키 대신 출처 이름을 보여 준다(키가 노출되지 않게). */
export function smartLabelOf(source: string): string {
  const key = smartMetaOf(source).labelKey;
  const text = tWorkspace(key);
  return text === key ? source : text;
}

interface Props {
  folders: ReadonlyArray<{ source: UsageSource; count: number }>;
  /** 지금 열려 있는 스마트 폴더. null 이면 루트. */
  active: UsageSource | null;
  onOpen: (source: UsageSource) => void;
}

export function WorkspaceSmartFolders({ folders, active, onOpen }: Props) {
  if (folders.length === 0) return null;

  return (
    <div className="ws-smart" aria-label={tWorkspace('workspace.smart.aria')}>
      <div className="ws-smart-label">{tWorkspace('workspace.smart.heading')}</div>
      <div className="ws-smart-row">
        {folders.map(({ source, count }) => {
          const meta = smartMetaOf(source);
          const Icon = meta.icon;
          return (
            <button
              key={source}
              type="button"
              className={`ws-smart-item${active === source ? ' ws-smart-item--on' : ''}`}
              aria-pressed={active === source}
              onClick={() => onOpen(source)}
            >
              <span className="ws-smart-icon" style={{ color: meta.tone }}>
                <Icon size={16} aria-hidden />
              </span>
              <span className="ws-smart-txt">
                <b>{smartLabelOf(source)}</b>
                <span className="ws-num">{count}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
